import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './FAQPage.css';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    question: 'What is GenLend?',
    answer: (
      <p>
        GenLend is an NCR-registered short-term credit provider designed specifically for students
        awaiting NSFAS payments. We offer quick cash loans from <strong>R200 to R750</strong> with
        approval in as little as 24 hours, so you can cover essentials while you wait.
      </p>
    ),
  },
  {
    question: 'Who can apply for a GenLend loan?',
    answer: (
      <p>
        Any registered South African tertiary student who is awaiting an NSFAS payment can apply.
        You'll need a valid student number, South African ID, and an active bank account.
      </p>
    ),
  },
  {
    question: 'How much can I borrow?',
    answer: (
      <p>
        You can borrow between <strong>R200 and R750</strong>. Use the loan calculator on our
        home page to see the full cost breakdown for any amount.
      </p>
    ),
  },
  {
    question: 'What can I use the loan for?',
    answer: (
      <div>
        <p>Anything you need to get through the month! Common examples include:</p>
        <ul>
          <li><strong>Unplanned travel</strong> — emergency trips home or bus fare back to campus</li>
          <li><strong>Medical costs</strong> — unexpected doctor visits or pharmacy runs</li>
          <li><strong>Varsity expenses</strong> — textbooks, printing, lab materials</li>
          <li><strong>Groceries &amp; essentials</strong> — food and daily necessities when NSFAS is delayed</li>
        </ul>
      </div>
    ),
  },
  {
    question: 'How long do I have to repay?',
    answer: (
      <p>
        The loan is repaid by the <strong>last day of the month</strong> in which it is paid out.
        Interest and the admin fee are worked out on the days left in that month.
      </p>
    ),
  },
  {
    question: 'What fees will I be charged?',
    answer: (
      <div>
        <p>All fees are disclosed upfront before you accept the loan. They include:</p>
        <ul>
          <li><strong>Interest</strong> — 5% × (days left until month-end ÷ 30) × the loan amount. The payout day itself is not counted.</li>
          <li><strong>Admin fee</strong> — R60 × (days left until month-end ÷ 30)</li>
          <li><strong>VAT</strong> — 15% on the admin fee only</li>
          <li><strong>Once-off initiation fee</strong> — R50, including when the loan is paid out on the last day of the month</li>
        </ul>
        <p>All fees are in line with the National Credit Act (NCA) maximums.</p>
      </div>
    ),
  },
  {
    question: 'Can you show me a full repayment example?',
    answer: (
      <div>
        <p>Absolutely. Here's a worked example for a <strong>R500 loan</strong> with <strong>15 days</strong> left until month-end:</p>
        <table className="faq-table">
          <thead>
            <tr>
              <th>Fee</th>
              <th>Calculation</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Loan Amount</td>
              <td>—</td>
              <td>R500.00</td>
            </tr>
            <tr>
              <td>Interest</td>
              <td>R500 × (15 ÷ 30) × 5%</td>
              <td>R12.50</td>
            </tr>
            <tr>
              <td>Admin fee</td>
              <td>R60 × (15 ÷ 30)</td>
              <td>R30.00</td>
            </tr>
            <tr>
              <td>VAT on admin fee</td>
              <td>R30 × 15%</td>
              <td>R4.50</td>
            </tr>
            <tr>
              <td>Initiation fee</td>
              <td>Once off</td>
              <td>R50.00</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}><strong>Total repayment</strong></td>
              <td><strong>R597.00</strong></td>
            </tr>
          </tfoot>
        </table>
        <p className="faq-note">
          All fees comply with National Credit Act (NCA) maximums. Your personalised quote may
          differ — use our <a href="/">loan calculator</a> for an accurate breakdown.
        </p>
      </div>
    ),
  },
  {
    question: 'How quickly will I receive my money?',
    answer: (
      <p>
        Once approved, funds are typically transferred to your bank account within
        <strong> 24 hours</strong>. In most cases, you'll have cash the same or next business day.
      </p>
    ),
  },
  {
    question: 'Is GenLend registered with the NCR?',
    answer: (
      <p>
        Yes. GenLend is a registered credit provider with the <strong>National Credit Regulator (NCR)</strong>.
        All our products comply with the National Credit Act, and all fees are within the legally
        permitted maximums.
      </p>
    ),
  },
  {
    question: 'What happens if I can\'t repay on time?',
    answer: (
      <p>
        We understand that things can go wrong. If you're struggling to make a payment, contact us
        as soon as possible. We'll work with you to find a solution. Ignoring your repayments can
        affect your credit record, so it's always best to reach out early.
      </p>
    ),
  },
  {
    question: 'Are there any hidden fees?',
    answer: (
      <p>
        <strong>No.</strong> Every fee is disclosed upfront before you sign your loan agreement.
        What you see in the calculator and in your loan offer is exactly what you'll pay — no
        surprises.
      </p>
    ),
  },
];

const FAQPage: React.FC = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-page">
      {/* Header */}
      <header className="faq-header">
        <div className="faq-header-inner">
          <button className="faq-back-btn" onClick={() => navigate('/')} type="button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <div className="faq-logo" onClick={() => navigate('/')} role="button" tabIndex={0}>
            <img src="/genlend-logo.png" alt="GenLend" className="faq-logo-icon" style={{ width: '52px', height: '52px', borderRadius: '50%' }} />
            <span className="faq-logo-text">GenLend</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="faq-hero">
        <h1>Frequently Asked Questions</h1>
        <p>Everything you need to know about GenLend student loans, fees, and repayments.</p>
      </section>

      {/* FAQ Accordion */}
      <section className="faq-list-section">
        <div className="faq-list">
          {faqs.map((item, index) => (
            <div
              key={index}
              className={`faq-item ${openIndex === index ? 'open' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggle(index)}
                type="button"
                aria-expanded={openIndex === index}
              >
                <span>{item.question}</span>
                <svg
                  className="faq-chevron"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              <div className="faq-answer">{item.answer}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="faq-cta">
        <h2>Still have questions?</h2>
        <p>Our team is here to help. Get in touch or start your application today.</p>
        <div className="faq-cta-buttons">
          <button className="faq-cta-primary" onClick={() => navigate('/auth')} type="button">
            Apply Now
          </button>
          <a href="https://wa.me/27722734008" className="faq-cta-secondary" target="_blank" rel="noopener noreferrer">
            WhatsApp 072 273 4008
          </a>
          <a href="mailto:info@genlend.co.za" className="faq-cta-secondary">
            Email Us
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="faq-footer">
        <p>© 2026 GenLend. All rights reserved. NCR Registered Credit Provider.</p>
      </footer>
    </div>
  );
};

export default FAQPage;
